import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Route, Routes, useLocation } from "react-router-dom";
import AssessmentScreen from "./assessments/assessment-layout";
import AssessmentHeader from "./headers/assessment-header";
import AssessmentSubheader from "./headers/assessment-subheader";
import AvatarModal from "./modal/assessment-avatar-modal";
import { useAssessments } from "./context/assessment-provider";
import AssessmentFormativesMap from "./formatives/formatives-course-map";
import "./assessments.scss";
import { IAssessment, sessionStorageKeys, StatusEnum } from "./types";
import useMyPathReferrer from "./hooks/useMyPathReferrer";

function anyDisabledDiagnosticsForSubject(assessments: IAssessment[], diagnosticArg: string) {
  return assessments.some(
    (assessment) =>
      assessment.subject.toLowerCase() === diagnosticArg.toLowerCase() &&
      assessment.disabled &&
      assessment.assessmentType === "diagnostic"
  );
}

function userHasntChangedToAConflictingSubject(subject: string, diagnosticArg: string) {
  return subject === "All" || subject?.toLowerCase() === diagnosticArg.toLowerCase();
}

function AssessmentCourseMap() {
  const { grade, launchDiagnostic, loading, assessments, subject } = useAssessments();
  const [viewStatus, setViewStatus] = useState<StatusEnum>(StatusEnum.IDLE);
  const [nextDiagnosticTestNumber, setNextDiagnosticTestNumber] = useState<number>(0);
  const [checkDiagnosticStatus, setCheckDiagnosticStatus] = useState<boolean>(true);
  const location = useLocation();
  const { returnToMyPathLandingPage } = useMyPathReferrer();

  function onHideNoDiagnosticModal() {
    sessionStorage.removeItem(sessionStorageKeys.diagnosticSubject);
    if (sessionStorage.getItem(sessionStorageKeys.referrer) === "MyPath") {
      // if the referrer is MyPath, redirect to liftoff
      returnToMyPathLandingPage();
    } else {
      setViewStatus(StatusEnum.IDLE);
    }
  }

  useEffect(() => {
    if (!loading && assessments && location) {
      if (location.state && (location.state as { launchDiagnostic: boolean }).launchDiagnostic) {
        setViewStatus(StatusEnum.DIAGNOSTIC);
      } else {
        const diagnosticArg = sessionStorage.getItem(sessionStorageKeys.diagnosticSubject);
        if (diagnosticArg && userHasntChangedToAConflictingSubject(subject, diagnosticArg) && checkDiagnosticStatus) {
          setCheckDiagnosticStatus(false);
          if (diagnosticArg === "none") {
            setViewStatus(StatusEnum.NO_DIAGNOSTIC);
          } else if (anyDisabledDiagnosticsForSubject(assessments, diagnosticArg)) {
            sessionStorage.setItem(sessionStorageKeys.multiDiagnostic, "true");
          } else {
            const diagnostic = assessments.find(
              (assessment) =>
                assessment.subject.toLowerCase() === diagnosticArg.toLowerCase() &&
                assessment.assessmentType === "diagnostic"
            );
            setNextDiagnosticTestNumber(diagnostic?.testNumber ?? 0);
            setViewStatus(StatusEnum.DIAGNOSTIC);
          }
        }
      }
    }
  }, [loading, assessments, location, subject, checkDiagnosticStatus]);

  if (viewStatus === StatusEnum.DIAGNOSTIC) {
    return (
      <AvatarModal
        grade={grade ?? 0}
        show={true}
        onHide={() => {
          launchDiagnostic(nextDiagnosticTestNumber, true);
        }}
        title="Test time!"
        text="It's time to take a test. Do your best."
      />
    );
  }

  if (viewStatus === StatusEnum.NO_DIAGNOSTIC) {
    return (
      <AvatarModal
        grade={grade ?? 0}
        show={true}
        onHide={() => {
          onHideNoDiagnosticModal();
        }}
        title="Your test is on the way!!"
        text="Your teacher will assign a test soon. Until then, ask your teacher for help."
      />
    );
  }

  return (
    <Container fluid style={{ padding: "0" }}>
      <AssessmentHeader />
      <Routes>
        <Route
          path="/"
          element={
            <>
              <AssessmentSubheader />
              <AssessmentScreen />
            </>
          }
        />
        <Route path="formatives/:id" element={<AssessmentFormativesMap />} />
      </Routes>
    </Container>
  );
}

export default AssessmentCourseMap;
